import * as React from 'react';
import { Dialog as BaseDialog } from '@base-ui-components/react/dialog';
import { cn } from '@/shared/lib/cn';
import { confirmDialog } from '@/shared/ui/ConfirmDialog/ConfirmDialog';
import { Scroller } from '@/shared/ui/Scroller';
import styles from './Dialog.module.css';

/**
 * shadcn-style Dialog поверх Base UI. Без множества пропов — composable через
 * subcomponents. Юзаешь так:
 *
 *   <Dialog open={open} onOpenChange={setOpen} dirty={hasChanges}>
 *     <DialogContent>
 *       <DialogTitle>...</DialogTitle>
 *       <DialogDescription>...</DialogDescription>
 *       <DialogFooter>
 *         <Dialog.Close render={<Button variant="ghost">Отмена</Button>} />
 *         <Button>OK</Button>
 *       </DialogFooter>
 *     </DialogContent>
 *   </Dialog>
 *
 * @use модальное окно. `dirty` включает подтверждение при закрытии с несохранёнными правками.
 */

const ACCIDENTAL_CLOSE = new Set(['escape-key', 'outside-press']);

const openDialogs: number[] = [];
let lastDialogId = 0;

let scrimMeta: HTMLMetaElement | null = null;

function syncDialogScrim() {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  if (openDialogs.length > 0) {
    if (scrimMeta) return;
    html.setAttribute('data-dialog-open', '');
    scrimMeta = document.createElement('meta');
    scrimMeta.name = 'theme-color';
    scrimMeta.content = getComputedStyle(html).getPropertyValue('--scrim-over-bg').trim();
    document.head.prepend(scrimMeta);
    return;
  }
  html.removeAttribute('data-dialog-open');
  scrimMeta?.remove();
  scrimMeta = null;
}

interface DialogLayer {
  id: number | null;
}

const DialogLayerContext = React.createContext<DialogLayer | null>(null);

function enterLayer(layer: DialogLayer): number {
  layer.id = ++lastDialogId;
  openDialogs.push(layer.id);
  syncDialogScrim();
  return openDialogs.length;
}

function leaveLayer(layer: DialogLayer) {
  const at = layer.id == null ? -1 : openDialogs.indexOf(layer.id);
  if (at >= 0) openDialogs.splice(at, 1);
  layer.id = null;
  syncDialogScrim();
}

function isTopDialog(layer: DialogLayer | null): boolean {
  if (layer?.id == null) return true;
  return openDialogs[openDialogs.length - 1] === layer.id;
}

export interface DialogProps extends React.ComponentProps<typeof BaseDialog.Root> {
  dirty?: boolean;
  unsavedWhat?: string;
}

export function Dialog({ dirty = false, unsavedWhat, onOpenChange, ...rest }: DialogProps) {
  const [layer] = React.useState<DialogLayer>(() => ({ id: null }));

  const handleOpenChange: NonNullable<DialogProps['onOpenChange']> = (open, details) => {
    if (!open && ACCIDENTAL_CLOSE.has(details.reason) && !isTopDialog(layer)) return;
    if (open || !dirty || !ACCIDENTAL_CLOSE.has(details.reason)) {
      onOpenChange?.(open, details);
      return;
    }
    void confirmDialog({
      title: 'Закрыть без сохранения?',
      description: unsavedWhat
        ? `${unsavedWhat} не сохранены и будут потеряны.`
        : 'Изменения не сохранены и будут потеряны.',
      confirmLabel: 'Закрыть',
      cancelLabel: 'Остаться',
      danger: true,
    }).then((leave) => {
      if (leave) onOpenChange?.(false, details);
    });
  };

  return (
    <DialogLayerContext.Provider value={layer}>
      <BaseDialog.Root onOpenChange={handleOpenChange} {...rest} />
    </DialogLayerContext.Provider>
  );
}

export const DialogTrigger = BaseDialog.Trigger;
export const DialogClose = BaseDialog.Close;

export const DialogContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof BaseDialog.Popup> & {
    backdropClassName?: string;
    flush?: boolean;
  }
>(function DialogContent({ children, className, backdropClassName, style, flush, ...rest }, ref) {
  const layer = React.useContext(DialogLayerContext);
  const [depth, setDepth] = React.useState(0);

  const popupRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (layer) {
        if (node) setDepth(enterLayer(layer));
        else {
          leaveLayer(layer);
          setDepth(0);
        }
      }
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [layer, ref],
  );

  const layerStyle = { '--dialog-layer': depth } as React.CSSProperties;

  return (
    <BaseDialog.Portal>
      <BaseDialog.Backdrop
        className={cn(styles.backdrop, backdropClassName)}
        style={layerStyle}
      />
      <BaseDialog.Popup
        ref={popupRef}
        className={cn(styles.popup, className)}
        style={{ ...layerStyle, ...style }}
        {...rest}
      >
        <Scroller
          className={cn(styles.popupScroll, flush && styles.popupScrollFlush)}
          viewportClassName={styles.popupViewport}
        >
          {children}
        </Scroller>
      </BaseDialog.Popup>
    </BaseDialog.Portal>
  );
});

export const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentProps<typeof BaseDialog.Title>
>(function DialogTitle({ className, ...rest }, ref) {
  return <BaseDialog.Title ref={ref} className={cn(styles.title, className)} {...rest} />;
});

export const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentProps<typeof BaseDialog.Description>
>(function DialogDescription({ className, ...rest }, ref) {
  return (
    <BaseDialog.Description ref={ref} className={cn(styles.description, className)} {...rest} />
  );
});

export function DialogFooter({
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn(styles.footer, className)} {...rest} />;
}
